import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { config } from '../config';
import axios from 'axios';

export interface OptionContract {
    id: string;
    symbol: string;
    underlying_symbol: string;
    root_symbol: string;
    type: 'call' | 'put';
    style: 'american' | 'european';
    expiration_date: string;
    strike_price: number | string;
    created_at: string;
    updated_at: string;
    status: 'active' | 'inactive';
    volume?: number | string;
    open_interest?: number | string;
}

export interface OptionChainData {
    symbol: string;
    expiration_date: string;
    expiration_dates?: string[];
    calls: {
        strike: number;
        volume: number;
        openInterest?: number;
        lastPrice?: number;
        impliedVolatility?: number;
    }[];
    puts: {
        strike: number;
        volume: number;
        openInterest?: number;
        lastPrice?: number;
        impliedVolatility?: number;
    }[];
}

export interface OptionsPrediction {
    symbol: string;
    prediction: 'bullish' | 'bearish' | 'neutral';
    callVolume: number;
    putVolume: number;
    volumeRatio: number;
    confidence: number;
    reasoning: string[];
}

interface OptionsState {
    loading: boolean;
    error: string | null;
}

const initialState: OptionsState = {
    loading: false,
    error: null,
};

export interface FetchOptionContractsParams {
    symbol: string;
    expirationDateGte?: string;
    expirationDateLte?: string;
    type?: 'call' | 'put';
    status?: 'active' | 'inactive';
}

export const fetchOptionContracts = createAsyncThunk<
    { symbol: string; contracts: OptionContract[] },
    FetchOptionContractsParams,
    { rejectValue: string }
>(
    'options/fetchOptionContracts',
    async (params, thunkAPI) => {
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('underlying_symbols', params.symbol);
            queryParams.append('status', params.status || 'active');
            queryParams.append('limit', '10000');

            if (params.expirationDateGte) {
                queryParams.append('expiration_date_gte', params.expirationDateGte);
            }
            if (params.expirationDateLte) {
                queryParams.append('expiration_date_lte', params.expirationDateLte);
            }
            if (params.type) {
                queryParams.append('type', params.type);
            }

            const contracts: OptionContract[] = [];
            let nextPageToken: string | undefined;

            do {
                if (nextPageToken) {
                    queryParams.set('page_token', nextPageToken);
                } else {
                    queryParams.delete('page_token');
                }

                const response = await axios({
                    baseURL: config.alpaca.baseUrl,
                    method: 'GET',
                    url: `v2/options/contracts?${queryParams.toString()}`,
                    headers: {
                        'APCA-API-KEY-ID': config.alpaca.keyId,
                        'APCA-API-SECRET-KEY': config.alpaca.secretKey,
                        'accept': 'application/json'
                    }
                });

                const pageContracts = response.data?.option_contracts || response.data?.contracts || response.data || [];
                if (Array.isArray(pageContracts)) {
                    contracts.push(...pageContracts);
                }
                nextPageToken = response.data?.next_page_token || undefined;
            } while (nextPageToken);

            return {
                symbol: params.symbol,
                contracts
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error instanceof Error ? error.message : 'Failed to fetch option contracts'
            );
        }
    }
);

const optionsSlice = createSlice({
    name: 'options',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchOptionContracts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchOptionContracts.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(fetchOptionContracts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch options';
            });
    }
});

export default optionsSlice;
